/**
 * Orquesta llamadas GraphQL reales del backend
 */

import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GraphService } from 'src/app/shared/lib/api/graph.service'; // TODO: Ver si se necesita un GraphService específico para RegistroCompra
import {
    GET_ALL_REGISTRO_COMPRA,
    GET_REGISTRO_COMPRA_BY_ID,
    VALIDATE_VINCULO_BY_ORDEN_COMPRA_ID,
    GET_ORDER_ALLOCATION_SUMMARIES,
} from '../graphql/registro-compra.queries';
import {
    CREATE_REGISTRO_COMPRA,
    UPDATE_REGISTRO_COMPRA,
    DELETE_REGISTRO_COMPRA,
    UPDATE_REGISTRO_COMPRA_HAS_DOCUMENT,
} from '../graphql/registro-compra.mutations';
import { DELETE_ORDEN_COMPRA_DE_RC_BY_ID_VINCULO } from '../../ordenes-compra';

@Injectable({ providedIn: 'root' })
export class RegistroCompraService {

    constructor(private graphService: GraphService) { }

    getOrderAllocationSummaries(
        orderType: 'OS' | 'OC',
        orderIds: string[],
    ): Observable<Array<{ orderId: string; consumedAmount: number }>> {
        if (orderIds.length === 0) return new Observable(subscriber => {
            subscriber.next([]);
            subscriber.complete();
        });
        return this.graphService
            .queryGraphQL<{ getOrderAllocationSummaries: Array<{ orderId: string; consumedAmount: number }> }>({
                query: GET_ORDER_ALLOCATION_SUMMARIES,
                variables: { orderType, orderIds },
            })
            .pipe(map(response => response.getOrderAllocationSummaries ?? []));
    }

    /**
     * GET - Obtener todos los RegistrosCompra
     * Retorna los datos tal como vienen del backend
     * [Refactorizado]
     */
    getAll(): Observable<any[]> {
        return this.graphService
            .queryGraphQL<{ getAllRegistroCompra: any[] }>({
                query: GET_ALL_REGISTRO_COMPRA,
                variables: {},
            })
            .pipe(
                map(response => response.getAllRegistroCompra || []),
                catchError(error => {
                    console.error('Error fetching registros compra:', error);
                    return throwError(() => new Error('Error al obtener registros de compra'));
                }),
            );
    }

    /**
     * GET - Obtener por ID
     * [Nuevo]
     */
    getById(id: string): Observable<any> {
        return this.graphService
            .queryGraphQL<{ getRegistroCompraById: any }>({
                query: GET_REGISTRO_COMPRA_BY_ID,
                variables: { id },
            })
            .pipe(
                map(response => response.getRegistroCompraById),
                catchError(error => {
                    console.error(`Error fetching registro compra ${id}:`, error);
                    return throwError(() => new Error('Error al obtener el registro de compra'));
                }),
            );
    }

    /**
     * POST - Crear nuevo RegistroCompra
     * Usa el formato raw del formulario (inputRegistroCompra)
     * [Refactorizado]
     */
    create(data: any): Observable<any> {
        return this.graphService
            .mutateGraphQL<any>({
                mutation: CREATE_REGISTRO_COMPRA,
                variables: { model: data },
            })
            .pipe(
                catchError(error => {
                    console.error('Error creating registro compra:', error);
                    return throwError(() => new Error(error.message || 'Error al crear el registro de compra'));
                }),
            );
    }

    /**
     * POST - Crear RegistroCompra desde formulario (raw data)
     * Alias de create() para compatibilidad
     * @deprecated Use create() instead
     */
    createRaw(data: any): Observable<any> {
        return this.create(data);
    }

    /**
     * PUT - Actualizar RegistroCompra
     * [Nuevo]
     */
    update(id: string, data: any): Observable<any> {
        return this.graphService
            .mutateGraphQL<any>({
                mutation: UPDATE_REGISTRO_COMPRA,
                variables: { id, changes: data },
            })
            .pipe(
                catchError(error => {
                    console.error(`Error updating registro compra ${id}:`, error);
                    return throwError(() => new Error(error.message || 'Error al actualizar registro de compra'));
                }),
            );
    }

    /**
     * DELETE - Eliminar RegistroCompra
     * [Refactorizado]
     */
    delete(id: string): Observable<void> {
        return this.graphService
            .mutateGraphQL<any>({
                mutation: DELETE_REGISTRO_COMPRA,
                variables: { id },
            })
            .pipe(
                map(() => undefined),
                catchError(error => {
                    console.error(`Error deleting registro compra ${id}:`, error);
                    return throwError(() => new Error(error.message || 'Error al eliminar registro de compra'));
                }),
            );
    }

    /**
     * Actualizar el estado de documento del RegistroCompra
     * [Refactorizado]
     */
    updateHasDocument(id: string, hasDocument: boolean): Observable<any> {
        return this.graphService
            .mutateGraphQL<any>({
                mutation: UPDATE_REGISTRO_COMPRA_HAS_DOCUMENT,
                variables: { id, hasDocument },
            })
            .pipe(
                map(() => undefined),
                catchError(error => {
                    console.error(`Error updating hasDocument for RC ${id}:`, error);
                    return throwError(() => new Error(error.message || 'Error al actualizar documento'));
                }),
            );
    }

    /**
     * Validar vínculo de Orden de Compra por ID
     * Retorna si está vinculado y el ID del vínculo
     * [Refactorizado]
     */
    validateVinculoByOrdenCompraId(idVinculado: string): Observable<any> {
        return this.graphService
            .queryGraphQL<any>({
                query: VALIDATE_VINCULO_BY_ORDEN_COMPRA_ID,
                variables: { idVinculado },
            })
            .pipe(
                map(response => response.validarVinculoByOrdenCompraId),
                catchError(error => {
                    console.error(`Error validating vinculo for OC ${idVinculado}:`, error);
                    return throwError(() => new Error(error.message ||  'Error al validar vínculo'));
                }),
            );
    }

    /**
     * Eliminar Órdenes de Compra de un Registro de Compra por IDs de vínculos
     * Use case para desvincular OCs de un RC
     * [Refactorizado]
     */
    deleteOrdenCompraDeRegistroCompraByIdVinculo(idVinculos: string[]): Observable<void> {
        return this.graphService
            .mutateGraphQL<any>({
                mutation: DELETE_ORDEN_COMPRA_DE_RC_BY_ID_VINCULO,
                variables: { IdVinculos: idVinculos },
            })
            .pipe(
                map(() => undefined),
                catchError(error => {
                    console.error('Error deleting OC vinculos from RC:', error);
                    return throwError(() => new Error('Error al eliminar vínculos de OC'));
                }),
            );
    }
}
